import { isAxiosError } from "axios";
import { LexicalEditor, SerializedEditorState } from "lexical";
import { useCallback, useEffect, useState } from "react";
import {
  convertFilesToAttachments,
  editorStateToPlainText,
  serializedEditorStateToHtmlWithCIDs,
} from "src/components/WysiwygEditor/lexical/utils/converters";
import {
  RichContentDocument,
  RichContentDocumentSchema,
} from "src/types/messages/content";
import * as RD from "src/types/remoteData";
import {
  DocumentStorageStatus,
  loading,
  Loading,
  ok,
  Ok,
  useDocumentStorage,
} from "./useDocumentStorage";

export function useMessageRichContent(
  emailEditor?: LexicalEditor | null,
  smsEditor?: LexicalEditor | null
) {
  const {
    getUploadUrl,
    upload,
    getDownloadUrl,
    download,
    getDeleteUrl,
    deleteFile,
  } = useDocumentStorage();

  const getMessageDocument = useCallback(
    async (props: {
      subject?: string;
      emailLexical?: SerializedEditorState;
      smsLexical?: SerializedEditorState;
      language?: string;
      fileAttachments?: File[];
    }) => {
      if (!emailEditor && !smsEditor) {
        return null;
      }

      const document: RichContentDocument = {
        language: props.language,
      };

      if (emailEditor) {
        const emailEditorState = props.emailLexical
          ? emailEditor.parseEditorState(props.emailLexical)
          : emailEditor.getEditorState();

        const emailLexical = props.emailLexical ?? emailEditorState.toJSON();

        const htmlWithCIDs = serializedEditorStateToHtmlWithCIDs(
          emailEditor,
          emailLexical
        );
        const attachments = await convertFilesToAttachments(
          props.fileAttachments ?? []
        );
        document.subject = props.subject;
        document.lexical = emailLexical;
        document.html = htmlWithCIDs?.html;
        document.text = editorStateToPlainText(emailEditorState);
        document.attachments = [
          ...(htmlWithCIDs?.attachments ?? []),
          ...attachments,
        ];
      }
      if (smsEditor) {
        const smsEditorState = props.smsLexical
          ? smsEditor.parseEditorState(props.smsLexical)
          : smsEditor.getEditorState();

        document.smsLexical = smsEditorState.toJSON();
        document.smsText = editorStateToPlainText(smsEditorState);
      }
      return document;
    },
    [emailEditor, smsEditor]
  );

  const uploadMessageDocument = useCallback(
    async (messageDocument: RichContentDocument) => {
      const uploadUrl = await getUploadUrl({
        filename: "message_payload.json",
        document_type: "MessagePayload",
        document_content_type: "application/json",
      });

      if (!uploadUrl) {
        throw new Error("Unable to get upload URL");
      }

      const uploadResponse = await upload(
        JSON.stringify(messageDocument),
        "application/json",
        uploadUrl
      );

      if (isAxiosError(uploadResponse)) {
        throw new Error("Unable to upload message payload", uploadResponse);
      }

      return {
        document_id: uploadUrl.document_id,
      };
    },
    [getUploadUrl, upload]
  );

  const uploadRichContent = useCallback(
    async ({
      subject,
      attachments,
      language = "en",
    }: {
      subject?: string;
      language?: string;
      attachments?: File[];
    }) => {
      const messageDocument = await getMessageDocument({
        subject: subject,
        language: language,
        fileAttachments: attachments,
      });
      if (messageDocument) {
        const { document_id } = await uploadMessageDocument(messageDocument);
        return { document_id, messageDocument };
      }

      return { document_id: null, messageDocument: null };
    },
    [getMessageDocument, uploadMessageDocument]
  );

  const downloadRichContent = useCallback(
    async (
      content_id: string | [string]
    ): Promise<Ok<RichContentDocument> | Loading> => {
      const documentId =
        typeof content_id === "string" ? content_id : content_id[0];
      const url = await getDownloadUrl(documentId);
      if (url.status === DocumentStorageStatus.OK) {
        return ok(
          RichContentDocumentSchema.parse(await download(url.data, "json"))
        );
      }
      return loading;
    },
    [getDownloadUrl, download]
  );

  const deleteRichContent = useCallback(
    async (contentId: string) => {
      const deleteUrl = await getDeleteUrl(contentId);
      if (deleteUrl) {
        await deleteFile(deleteUrl);
      }
    },
    [getDeleteUrl, deleteFile]
  );

  return {
    getMessageDocument,
    uploadMessageDocument,
    uploadRichContent,
    downloadRichContent,
    deleteRichContent,
  };
}

export function useMessageRichContentQuery(
  contentId: string | undefined | null,
  skip = false
) {
  const { downloadRichContent } = useMessageRichContent();

  const [result, setResult] = useState<
    RD.RemoteData<Error, RichContentDocument | null>
  >(RD.notAsked());

  useEffect(() => {
    if (skip) {
      setResult(RD.notAsked());
      return;
    }

    if (!contentId) {
      setResult(RD.success(null));
      return;
    }

    downloadRichContent(contentId)
      .then((richContent) => {
        if (richContent.status === DocumentStorageStatus.OK) {
          setResult(RD.success(richContent.data));
        } else {
          setResult(RD.loading());
        }
      })
      .catch((error) => {
        setResult((prev) => RD.failure(error, prev));
      });

    setResult((prev) => RD.loading(prev));
  }, [contentId, downloadRichContent, skip]);

  return { remoteData: result };
}
